/* src/pages/AdminUserManagement.jsx
 * Back Office - User Management
 * View and manage all users, subscriptions, trials, etc.
 */

import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../utils/admin';
import { TIER_SUBMISSION_LIMITS, getSubmissionLimitForTier } from '../utils/tiers';
import { AdminUsersTableSkeleton } from '../components/Skeleton';

function formatSignupSource(user) {
  const s = user.signupAttribution || user.attribution;
  if (!s) return 'Direct';
  if (s.utmSource || s.utms?.utm_source) {
    const src = s.utmSource || s.utms.utm_source;
    const med = s.utmMedium || s.utms?.utm_medium;
    return med ? `${src} / ${med}` : src;
  }
  if (s.gclid || s.clickIds?.gclid) return 'Google Ads';
  if (s.fbclid || s.clickIds?.fbclid) return 'Meta';
  if (s.landingPage || s.landingUrl) {
    try {
      return new URL(s.landingPage || s.landingUrl).hostname;
    } catch {
      return 'Direct';
    }
  }
  return 'Direct';
}

export default function AdminUserManagement() {
  const { currentUser, userProfile, loadUserProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filter, setFilter] = useState('all'); // all, trial, active, canceled, free
  const [searchEmail, setSearchEmail] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null); // Track which user is being updated
  const [updatingLimitUserId, setUpdatingLimitUserId] = useState(null); // Track which user's limit is being updated

  useEffect(() => {
    // Check if user is admin
    if (currentUser && userProfile !== undefined) {
      const hasAccess = isAdmin(currentUser, userProfile);
      if (!hasAccess) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      loadUsers();
    }
  }, [filter, currentUser, userProfile]);

  async function loadUsers() {
    try {
      setError(null);

      console.log('[ADMIN] Loading users from Firestore...');
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);

      const usersList = [];
      snapshot.forEach((doc) => {
        const userData = doc.data();
        const userObj = {
          id: doc.id,
          ...userData,
          // Convert Firestore Timestamps to dates
          createdAt: userData.createdAt?.toDate ? userData.createdAt.toDate() : userData.createdAt,
          trialStartDate: userData.trialStartDate?.toDate ? userData.trialStartDate.toDate() : userData.trialStartDate,
          trialEndDate: userData.trialEndDate?.toDate ? userData.trialEndDate.toDate() : userData.trialEndDate,
        };
        usersList.push(userObj);
        console.log('[ADMIN] Loaded user:', { id: doc.id, email: userData.email, tier: userData.tier });
      });

      // Sort by creation date (newest first)
      usersList.sort((a, b) => {
        const dateA = a.createdAt || new Date(0);
        const dateB = b.createdAt || new Date(0);
        return dateB - dateA;
      });

      console.log('[ADMIN] Setting users state with', usersList.length, 'users');
      setUsers(usersList);
      console.log('[ADMIN] Users state updated');
    } catch (err) {
      console.error('[ADMIN] Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  function filterUsers() {
    let filtered = users;

    // Filter by status
    if (filter === 'trial') {
      filtered = filtered.filter(u => u.trialActive === true);
    } else if (filter === 'active') {
      filtered = filtered.filter(u => u.subscriptionStatus === 'active' && u.subscriptionId);
    } else if (filter === 'canceled') {
      filtered = filtered.filter(u => u.subscriptionStatus === 'canceled');
    } else if (filter === 'free') {
      filtered = filtered.filter(u => 
        !u.trialActive && 
        (!u.subscriptionStatus || u.subscriptionStatus === 'canceled') &&
        (!u.subscriptionId)
      );
    }

    // Filter by search
    if (searchEmail) {
      filtered = filtered.filter(u => 
        u.email?.toLowerCase().includes(searchEmail.toLowerCase())
      );
    }

    return filtered;
  }

  async function updateUserTier(userId, newTier) {
    if (!userId || !newTier) {
      console.error('[ADMIN] Missing userId or newTier:', { userId, newTier });
      setError('Invalid user ID or tier value');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setUpdatingUserId(userId);

      console.log('[ADMIN] Updating user tier:', { userId, newTier, currentUserId: currentUser?.uid, currentUserEmail: currentUser?.email });

      const userRef = doc(db, 'users', userId);
      const submissionLimit = TIER_SUBMISSION_LIMITS[newTier] ?? TIER_SUBMISSION_LIMITS.free;
      const updates = {
        tier: newTier,
        submissionLimit,
      };
      console.log('[ADMIN] Attempting Firestore update to:', userRef.path, { tier: newTier, submissionLimit });
      await updateDoc(userRef, updates);
      console.log('[ADMIN] ✅ Firestore update successful');

      setSuccess(`User tier updated to ${newTier}`);
      
      // Force a fresh fetch from Firestore (bypass cache)
      console.log('[ADMIN] Reloading users list...');
      
      // Reload users - this will update state asynchronously
      await loadUsers();
      console.log('[ADMIN] ✅ Users list reloaded');
      
      // Also reload current user profile if it's the current user
      if (currentUser && userId === currentUser.uid && loadUserProfile) {
        console.log('[ADMIN] Reloading current user profile...');
        await loadUserProfile(currentUser.uid);
        console.log('[ADMIN] ✅ User profile reloaded');
        // Refresh page to ensure all state is updated
        setTimeout(() => {
          window.location.reload();
        }, 300);
      } else {
        // For other users, just wait a bit for state to update, then force re-render
        // The key prop on the select should handle the re-render
        setTimeout(() => {
          // Force a state update by toggling a dummy state
          setUpdatingUserId(null);
          setUpdatingUserId(userId);
          setTimeout(() => setUpdatingUserId(null), 100);
        }, 200);
      }
    } catch (err) {
      console.error('[ADMIN] ❌ Error updating user tier:', err);
      console.error('[ADMIN] Error code:', err.code);
      console.error('[ADMIN] Error message:', err.message);
      console.error('[ADMIN] Error stack:', err.stack);
      console.error('[ADMIN] Full error object:', JSON.stringify(err, null, 2));
      
      let errorMessage = 'Failed to update user tier';
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. Make sure you have admin access. Check browser console for details.';
      } else if (err.code === 'not-found') {
        errorMessage = 'User document not found.';
      } else if (err.message) {
        errorMessage = `Failed to update: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setUpdatingUserId(null);
    }
  }

  async function updateSubscriptionStatus(userId, newStatus) {
    try {
      setError(null);
      setSuccess(null);

      const updates = { subscriptionStatus: newStatus };
      if (newStatus === 'active' || newStatus === 'trialing') {
        updates.trialActive = false;
      }
      await updateDoc(doc(db, 'users', userId), updates);

      setSuccess(`Subscription status updated to ${newStatus}`);
      loadUsers();
    } catch (err) {
      console.error('Error updating subscription status:', err);
      setError('Failed to update subscription status');
    }
  }

  async function updateUserSubmissionLimit(userId, value) {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 1) return;
    try {
      setError(null);
      setSuccess(null);
      setUpdatingLimitUserId(userId);
      await updateDoc(doc(db, 'users', userId), { submissionLimit: num });
      setSuccess('Submission limit updated');
      await loadUsers();
      if (currentUser && userId === currentUser.uid && loadUserProfile) {
        await loadUserProfile(currentUser.uid);
      }
    } catch (err) {
      console.error('Error updating submission limit:', err);
      setError('Failed to update submission limit');
    } finally {
      setUpdatingLimitUserId(null);
    }
  }

  async function extendTrial(userId, days) {
    try {
      setError(null);
      setSuccess(null);

      const userDoc = doc(db, 'users', userId);
      const user = users.find(u => u.id === userId);
      
      if (!user) return;

      const currentEnd = user.trialEndDate || new Date();
      const newEnd = new Date(currentEnd);
      newEnd.setDate(newEnd.getDate() + days);

      await updateDoc(userDoc, {
        trialEndDate: Timestamp.fromDate(newEnd),
        trialActive: true,
      });

      setSuccess(`Trial extended by ${days} days`);
      loadUsers();
    } catch (err) {
      console.error('Error extending trial:', err);
      setError('Failed to extend trial');
    }
  }

  function formatDate(date) {
    if (!date) return 'N/A';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function getDaysRemaining(trialEndDate) {
    if (!trialEndDate) return null;
    const end = trialEndDate instanceof Date ? trialEndDate : new Date(trialEndDate);
    const now = new Date();
    const diff = end - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  }

  function getStatusBadge(user) {
    // Show tier-based status - the tier is what matters
    const tier = user.tier || 'free';
    
    if (['growth', 'professional', 'enterprise', 'starter', 'business'].includes(tier)) {
      // Paid tier - show tier name
      const tierName = tier.charAt(0).toUpperCase() + tier.slice(1);
      
      if (user.trialActive) {
        const days = getDaysRemaining(user.trialEndDate);
        return {
          text: `${tierName} (Trial - ${days}d)`,
          color: '#ffc107',
          bgColor: '#fff3cd',
        };
      } else if (user.subscriptionStatus === 'active' && user.subscriptionId) {
        return {
          text: `${tierName} (Active)`,
          color: '#155724',
          bgColor: '#d4edda',
        };
      } else if (user.subscriptionStatus === 'canceled') {
        return {
          text: `${tierName} (Canceled)`,
          color: '#721c24',
          bgColor: '#f8d7da',
        };
      } else if (user.subscriptionStatus === 'past_due') {
        return {
          text: `${tierName} (Past due)`,
          color: '#856404',
          bgColor: '#fff3cd',
        };
      } else {
        // Paid tier but no active subscription
        return {
          text: tierName,
          color: '#856404',
          bgColor: '#fff3cd',
        };
      }
    } else if (user.trialActive) {
      // Free tier but in trial
      const days = getDaysRemaining(user.trialEndDate);
      return {
        text: `Trial (${days}d left)`,
        color: '#ffc107',
        bgColor: '#fff3cd',
      };
    } else {
      // Free tier, no trial
      return {
        text: 'Free',
        color: '#856404',
        bgColor: '#fff3cd',
      };
    }
  }

  const filteredUsers = filterUsers();

  if (loading) {
    return (
      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '2rem' }}>User Management</h1>
        <AdminUsersTableSkeleton rows={6} />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          background: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '8px',
          padding: '2rem',
          textAlign: 'center',
        }}>
          <h2 style={{ marginTop: 0, color: '#856404' }}>🔒 Access Denied</h2>
          <p style={{ fontSize: '1.1rem', color: '#856404', marginBottom: '1rem' }}>
            User Management is restricted to administrators only.
          </p>
          <p style={{ color: '#666' }}>
            Only administrators can access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '2rem' }}>👥 User Management (Back Office)</h1>

      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          color: '#c33',
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          background: '#efe',
          border: '1px solid #cfc',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          color: '#3c3',
        }}>
          {success}
        </div>
      )}

      {/* Filters and Search */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '1.5rem',
        marginBottom: '2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '1rem',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Search by Email
          </label>
          <input
            type="text"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            placeholder="user@example.com"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
            }}
          />
        </div>

        <div style={{ minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Filter by Status
          </label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
            }}
          >
            <option value="all">All Users</option>
            <option value="trial">Trial Active</option>
            <option value="active">Active Subscriptions</option>
            <option value="canceled">Canceled</option>
            <option value="free">Free (No Subscription)</option>
          </select>
        </div>

        <div style={{ alignSelf: 'flex-end' }}>
          <button
            onClick={loadUsers}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#1e40af',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e40af' }}>
            {users.length}
          </div>
          <div style={{ color: '#666' }}>Total Users</div>
        </div>
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffc107' }}>
            {users.filter(u => u.trialActive).length}
          </div>
          <div style={{ color: '#666' }}>Active Trials</div>
        </div>
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
            {users.filter(u => u.subscriptionStatus === 'active' && u.subscriptionId).length}
          </div>
          <div style={{ color: '#666' }}>Active Subscriptions</div>
        </div>
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#6c757d' }}>
            {users.filter(u => !u.trialActive && (!u.subscriptionStatus || u.subscriptionStatus === 'canceled') && !u.subscriptionId).length}
          </div>
          <div style={{ color: '#666' }}>Free Users</div>
        </div>
      </div>

      {/* Users Table */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}>
        <h2 style={{ marginTop: 0 }}>Users ({filteredUsers.length})</h2>

        {filteredUsers.length === 0 ? (
          <p style={{ color: '#666' }}>No users found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Tier</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Limit</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Plan</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Trial End</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Signup source</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Created</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Actions</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Admin</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const status = getStatusBadge(user);
                  // Use tier in key to force re-render when tier changes
                  return (
                    <tr key={`${user.id}-${user.tier}-${getSubmissionLimitForTier(user.tier, user.submissionLimit)}`} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '1rem' }}>
                        <strong>{user.email || 'N/A'}</strong>
                        {user.id === currentUser?.uid && (
                          <span style={{
                            marginLeft: '0.5rem',
                            fontSize: '0.85rem',
                            color: '#1e40af',
                          }}>(You)</span>
                        )}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          background: status.bgColor,
                          color: status.color,
                        }}>
                          {status.text}
                        </span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <select
                          key={`tier-${user.id}-${user.tier}`} // Force re-render when tier changes
                          value={user.tier || 'free'}
                          onChange={(e) => {
                            console.log('[ADMIN] Tier dropdown changed:', { userId: user.id, newTier: e.target.value, currentTier: user.tier });
                            updateUserTier(user.id, e.target.value);
                          }}
                          disabled={updatingUserId === user.id}
                          style={{
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem',
                            opacity: updatingUserId === user.id ? 0.6 : 1,
                            cursor: updatingUserId === user.id ? 'wait' : 'pointer',
                          }}
                        >
                          <option value="free">Free</option>
                          <option value="growth">Growth</option>
                          <option value="professional">Professional</option>
                          <option value="enterprise">Enterprise</option>
                        </select>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <input
                          type="number"
                          min={1}
                          defaultValue={getSubmissionLimitForTier(user.tier, user.submissionLimit)}
                          onBlur={(e) => {
                            const val = e.target.value.trim();
                            if (val === '') return;
                            const num = parseInt(val, 10);
                            const currentDisplay = getSubmissionLimitForTier(user.tier, user.submissionLimit);
                            if (num === currentDisplay) return;
                            if (!isNaN(num) && num >= 1) updateUserSubmissionLimit(user.id, num);
                          }}
                          disabled={updatingLimitUserId === user.id}
                          style={{
                            width: '4.5rem',
                            padding: '0.35rem 0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem',
                          }}
                          title="Custom submission limit (testers, special pricing). Blur to save."
                        />
                        {updatingLimitUserId === user.id && (
                          <span style={{ marginLeft: '0.35rem', fontSize: '0.8rem', color: '#666' }}>Saving...</span>
                        )}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {user.subscriptionPlan || '-'}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {user.trialEndDate ? formatDate(user.trialEndDate) : '-'}
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.85rem', color: '#555' }}>
                        {formatSignupSource(user)}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {user.createdAt ? formatDate(user.createdAt) : '-'}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          {user.trialActive && (
                            <>
                              <button
                                onClick={() => extendTrial(user.id, 7)}
                                style={{
                                  padding: '0.25rem 0.75rem',
                                  background: '#ffc107',
                                  color: '#000',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                }}
                              >
                                +7d
                              </button>
                              <button
                                onClick={() => extendTrial(user.id, 30)}
                                style={{
                                  padding: '0.25rem 0.75rem',
                                  background: '#ffc107',
                                  color: '#000',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                }}
                              >
                                +30d
                              </button>
                            </>
                          )}
                          <select
                            value={user.subscriptionStatus || 'canceled'}
                            onChange={(e) => updateSubscriptionStatus(user.id, e.target.value)}
                            style={{
                              padding: '0.5rem',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                            }}
                          >
                            <option value="trialing">Trialing</option>
                            <option value="active">Active</option>
                            <option value="canceled">Canceled</option>
                            <option value="past_due">Past Due</option>
                          </select>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={user.isAdmin || false}
                              onChange={async (e) => {
                                try {
                                  await updateDoc(doc(db, 'users', user.id), {
                                    isAdmin: e.target.checked,
                                  });
                                  setSuccess(`Admin status ${e.target.checked ? 'granted' : 'removed'} for ${user.email}`);
                                  loadUsers();
                                } catch (err) {
                                  console.error('Error updating admin status:', err);
                                  setError('Failed to update admin status');
                                }
                              }}
                            />
                            <span>Admin</span>
                          </label>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {user.isAdmin ? (
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            background: '#d4edda',
                            color: '#155724',
                          }}>
                            ✓ Admin
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
